import { useCallback, useEffect, useState } from "react";
import { Notification } from "@rio-cloud/rio-uikit";
import { deleteTransactionsBatch } from "../services/form";

interface UseTransactionsBatchDeleteOptions {
  resetOn: unknown;
  onDeleted: () => void;
  errorMessage: string;
}

export const useTransactionsBatchDelete = ({ resetOn, onDeleted, errorMessage }: UseTransactionsBatchDeleteOptions) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  useEffect(() => clearSelection(), [resetOn, clearSelection]);

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const toggleAll = useCallback((checked: boolean, ids: string[]) => setSelectedIds(checked ? ids : []), []);

  const openConfirm = () => setShowConfirm(true);

  const closeConfirm = () => setShowConfirm(false);

  const confirmDelete = async () => {
    if (selectedIds.length === 0 || isDeleting) return;

    setIsDeleting(true);
    try {
      const response = await deleteTransactionsBatch(selectedIds.map(Number));
      const deletedCount = response?.data?.deletedCount ?? 0;
      const notFoundCount = response?.data?.notFoundIds?.length ?? 0;

      Notification.success(response?.message || `${deletedCount} transação(ões) excluída(s) com sucesso.`);
      if (notFoundCount > 0) {
        Notification.warning(`${notFoundCount} transação(ões) não encontrada(s) para exclusão.`);
      }

      clearSelection();
      setShowConfirm(false);
      onDeleted();
    } catch {
      Notification.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    selectedIds,
    showConfirm,
    isDeleting,
    toggleRow,
    toggleAll,
    clearSelection,
    openConfirm,
    closeConfirm,
    confirmDelete,
  };
};
